import proj4 from "proj4";
import { LineString, MultiLineString } from "ol/geom";
import { Coordinate } from "ol/coordinate";
import { register } from "ol/proj/proj4";
import { transform } from "ol/proj";
import { normalizeString } from "services/normalizedString";
import { TSuggestedResults, TSuggestedResultsItem } from "../types/arcgis.type";

proj4.defs(
    "EPSG:5514",
    "+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 +alpha=30.28813972222222 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=570.69,85.69,462.84,4.99821,1.58676,5.2611,3.56 +units=m +no_defs"
);
register(proj4);

class ArcgisService {
    private baseUrl = "https://gs-pub.praha.eu/arcgis/rest";
    private streetSearchUrl = `${this.baseUrl}/services/app_vyhledavani/vyhledavani/MapServer/7/query`;
    private addressSearchUrl = `${this.baseUrl}/services/app_vyhledavani/vyhledavani/MapServer/0/query`;

    private getHybridCenter(paths: [number, number][][]): Coordinate {
        const multiLine = new MultiLineString(paths);
        const flatCoords = multiLine.getCoordinates().flat();
        const joinedLine = new LineString(flatCoords);
        return joinedLine.getCoordinateAt(0.5);
    }

    protected async getSuggestions(url: string): Promise<any> {
        const res = await fetch(url, { method: "GET" });
        if (!res.ok) {
            throw new Error(`Network error: ${res.status}`);
        }
        return res.json();
    }

    public async getSuggestedByStreetResults(query: string): Promise<TSuggestedResults> {
        const outFields = "MKN,MKN4SEARCH,OBJECTID,NAZEV,NAZEV4SEARCH,VLOZENO,SHAPE.LEN";
        const normalizedQuery = normalizeString(query);

        const url = `${this.streetSearchUrl}?f=json&outFields=${outFields}&where=MKN4SEARCH LIKE '%25${encodeURIComponent(
            normalizedQuery
        )}%25'`;

        const data = await this.getSuggestions(url);
        const features = data.features || [];

        const grouped: { [key: string]: any[] } = {};
        features.forEach((feature: any) => {
            const name = feature.attributes?.MKN;
            if (!name) return;

            if (!grouped[name]) {
                grouped[name] = [];
            }
            grouped[name].push(feature);
        });

        const suggestions = Object.entries(grouped).map(([name, group]) => {
            const allPaths = group.flatMap((g) => g.geometry?.paths || []);
            const center = this.getHybridCenter(group.flatMap((g) => g.geometry.paths));

            const centerTransform = (center && center.length > 0 && transform(center, "EPSG:5514", "EPSG:4326")) || [0, 0];

            return {
                name,
                address: name,
                location: {
                    x: centerTransform[0],
                    y: centerTransform[1],
                },
                paths: allPaths,
            } as TSuggestedResultsItem;
        });

        return { suggestions };
    }

    public async getSuggestedByAddressResults(query: string): Promise<TSuggestedResults> {
        const outFields = "ADRESA,ADRESA4SEARCH,OBJECTID,CISLODOMOVNI,CISLOORIENTACNI,PSC,VO_KOD,ULICE,NAZEVULICE,VLOZENO,SHAPE";
        const normalizedQuery = normalizeString(query);

        const addressRegex = /^(\D+) (\d+)$/;
        const matches = normalizedQuery.match(addressRegex);

        let url = `${this.addressSearchUrl}?f=json&outFields=${outFields}&where=ADRESA4SEARCH LIKE '%25${encodeURIComponent(
            normalizedQuery
        )}%25'`;

        if (matches.length > 0) {
            url = `${this.addressSearchUrl}?f=json&outFields=${outFields}&where=ADRESA4SEARCH LIKE '%25${encodeURIComponent(
                matches[1]
            )}%25' and (CISLODOMOVNI = ${matches[2]} or CISLOORIENTACNI = ${matches[2]})`;
        }

        const data = await this.getSuggestions(url);
        const features = data.features || [];

        const grouped: { [key: string]: any } = {};
        features.forEach((feature: any) => {
            const objectId = feature.attributes?.OBJECTID;

            if (!objectId) return;

            if (!grouped[objectId]) {
                grouped[objectId] = feature;
            }
        });

        const suggestions = Object.entries(grouped).map(([_, group]) => {
            const centerTransform = transform([group.geometry.x, group.geometry.y], "EPSG:5514", "EPSG:4326") || [0, 0];

            return {
                name: group.attributes?.ADRESA || "",
                address: group.attributes?.ADRESA || "",
                location: {
                    x: centerTransform[0],
                    y: centerTransform[1],
                },
            } as TSuggestedResultsItem;
        });

        return { suggestions };
    }

    public getSuggestedFeature(suggestion: TSuggestedResultsItem): any {
        return Promise.resolve({
            features: [
                {
                    type: "Feature",
                    geometry: {
                        coordinates: [suggestion.location.x, suggestion.location.y],
                        paths: suggestion.paths,
                        type: suggestion.paths.length > 0 ? "LineString" : "Point",
                    },
                    properties: {
                        name: suggestion.name,
                        address: suggestion.address,
                    },
                },
            ],
        });
    }

}

export default ArcgisService;
